import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Link as LinkIcon, Check, Hash, X, Lock, Eye, EyeOff, Wand2, Zap, Sparkles, Gem } from "lucide-react";

const GENRES = [
  { key: "melodic house",      label: "Melodic House" },
  { key: "melodic techno",     label: "Melodic Techno" },
  { key: "progressive house",  label: "Progressive House" },
  { key: "deep house",         label: "Deep / Afro House" },
];

const RENDER_SPEEDS = [
  { id: "fast", label: "Veo 3.1 Fast", Icon: Zap, hint: "Google Veo 3.1 Fast · 8s native 1080p clip looped to 15s · ~1-3 min · Universal Key", recommended: true },
  { id: "pro",  label: "Veo 3.1 Pro",  Icon: Gem, hint: "Google Veo 3.1 · 8s native 1080p · highest fidelity · ~3-6 min · Universal Key" },
];

export default function Settings() {
  const { user } = useAuth();
  const [artistUrl, setArtistUrl] = useState("");
  const [savedArtistId, setSavedArtistId] = useState("");
  const [artistDescription, setArtistDescription] = useState("");
  const [descDirty, setDescDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingDesc, setSavingDesc] = useState(false);

  // Hashtag editor.
  const [customHashtags, setCustomHashtags] = useState([]);
  const [tagDraft, setTagDraft] = useState("");
  const [defaultGenre, setDefaultGenre] = useState("melodic techno");
  const [savingTags, setSavingTags] = useState(false);

  // Batch render speed (controls Sora model used by weekly + Blast generators).
  const [batchSpeed, setBatchSpeed] = useState("fast");
  const [savingSpeed, setSavingSpeed] = useState(false);

  // Password change.
  const [pwCurrent, setPwCurrent] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwConfirm, setPwConfirm] = useState("");
  const [pwShow, setPwShow] = useState(false);
  const [savingPw, setSavingPw] = useState(false);

  const [suggesting, setSuggesting] = useState(false);

  useEffect(() => {
    api.get("/profile").then((r) => {
      setSavedArtistId(r.data.spotify_artist_id || "");
      setArtistDescription(r.data.artist_description || "");
      setCustomHashtags(r.data.custom_hashtags || []);
      setDefaultGenre(r.data.default_genre || "melodic techno");
      setBatchSpeed(r.data.batch_render_speed || "fast");
    });
  }, []);

  const saveBatchSpeed = async (next) => {
    if (next === batchSpeed) return;
    setBatchSpeed(next);
    setSavingSpeed(true);
    try {
      await api.patch("/profile", { batch_render_speed: next });
      const lbl = RENDER_SPEEDS.find((s) => s.id === next)?.label || next;
      toast.success(`Batch render speed → ${lbl}.`);
    } catch {
      toast.error("Failed to save");
    } finally {
      setSavingSpeed(false);
    }
  };

  const saveArtist = async () => {
    if (!artistUrl.trim()) {
      toast.error("Paste your Spotify artist URL");
      return;
    }
    setSaving(true);
    try {
      const res = await api.post("/connections/spotify/artist", { spotify_artist_url: artistUrl });
      toast.success(`Synced ${res.data.releases_synced} releases from Spotify.`);
      setSavedArtistId(res.data.artist_id);
      setArtistUrl("");
    } catch (e) {
      const detail = e?.response?.data?.detail || "Failed to sync";
      toast.error(typeof detail === "string" ? detail : "Failed to sync");
    } finally {
      setSaving(false);
    }
  };

  const saveDescription = async () => {
    setSavingDesc(true);
    try {
      await api.patch("/profile", { artist_description: artistDescription });
      toast.success("Artist description saved — Sora 2 will use this.");
      setDescDirty(false);
    } catch {
      toast.error("Failed to save");
    } finally {
      setSavingDesc(false);
    }
  };

  const normalizeTag = (raw) => {
    const s = (raw || "").trim().replace(/^#+/, "").replace(/[^a-zA-Z0-9_]/g, "").slice(0, 32).toLowerCase();
    return s ? `#${s}` : "";
  };

  const addTag = (raw) => {
    const t = normalizeTag(raw);
    if (!t) return;
    if (customHashtags.includes(t)) {
      toast.error("Already in your list");
      return;
    }
    if (customHashtags.length >= 50) {
      toast.error("Max 50 custom tags");
      return;
    }
    setCustomHashtags([...customHashtags, t]);
    setTagDraft("");
  };

  const removeTag = (t) => setCustomHashtags(customHashtags.filter((x) => x !== t));

  const onTagKey = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addTag(tagDraft);
    } else if (e.key === "Backspace" && !tagDraft && customHashtags.length) {
      setCustomHashtags(customHashtags.slice(0, -1));
    }
  };

  const onTagPaste = (e) => {
    const text = e.clipboardData.getData("text");
    if (!text.includes(" ") && !text.includes(",") && !text.includes("\n")) return;
    e.preventDefault();
    const parts = text.split(/[\s,]+/).map(normalizeTag).filter(Boolean);
    const merged = [...customHashtags];
    for (const p of parts) {
      if (!merged.includes(p) && merged.length < 50) merged.push(p);
    }
    setCustomHashtags(merged);
    setTagDraft("");
  };

  const saveTags = async () => {
    setSavingTags(true);
    try {
      const r = await api.patch("/profile", {
        custom_hashtags: customHashtags,
        default_genre: defaultGenre,
      });
      setCustomHashtags(r.data.custom_hashtags || []);
      toast.success("Hashtag preferences saved.");
    } catch {
      toast.error("Failed to save tags");
    } finally {
      setSavingTags(false);
    }
  };

  const suggestTags = async () => {
    setSuggesting(true);
    try {
      const r = await api.post("/hashtag-suggest", {
        genre: defaultGenre, count: 20,
      });
      const incoming = (r.data.hashtags || []).filter((t) => !customHashtags.includes(t));
      const merged = [...customHashtags, ...incoming].slice(0, 50);
      setCustomHashtags(merged);
      toast.success(
        `Added ${incoming.length} AI-suggested tag${incoming.length === 1 ? "" : "s"} · ${r.data.source === "ai" ? "Claude" : "preset"}.`
      );
    } catch {
      toast.error("Suggestion failed");
    } finally {
      setSuggesting(false);
    }
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    if (pwNew.length < 8) {
      toast.error("New password must be at least 8 characters.");
      return;
    }
    if (pwNew !== pwConfirm) {
      toast.error("New passwords don't match.");
      return;
    }
    setSavingPw(true);
    try {
      await api.post("/auth/change-password", { current_password: pwCurrent, new_password: pwNew });
      toast.success("Password updated.");
      setPwCurrent(""); setPwNew(""); setPwConfirm("");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Failed to change password";
      toast.error(typeof detail === "string" ? detail : "Failed to change password");
    } finally {
      setSavingPw(false);
    }
  };

  return (
    <div className="fade-in" data-testid="settings-page">
      <div className="mb-10">
        <div className="label-mono mb-4">Settings</div>
        <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-tight tracking-tight">Profile.</h1>
      </div>

      <div className="grid md:grid-cols-2 gap-6 sm:gap-10 max-w-4xl">
        <div className="panel p-5 sm:p-8">
          <div className="label-mono mb-6">Account</div>
          <div className="flex items-center gap-4 mb-6">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-14 h-14 rounded-full border border-white/10" />
            ) : (
              <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center font-mono">
                {user?.name?.slice(0, 2)?.toUpperCase() || "U"}
              </div>
            )}
            <div className="min-w-0">
              <div className="font-display text-xl sm:text-2xl truncate" data-testid="settings-name">{user?.name || "—"}</div>
              <div className="label-mono mt-1 break-all" data-testid="settings-email">{user?.email}</div>
            </div>
          </div>
          <div className="label-mono">User ID</div>
          <div className="font-mono text-xs mt-1 text-white/55 break-all" data-testid="settings-userid">
            {user?.user_id}
          </div>
        </div>

        <div className="panel p-5 sm:p-8">
          <div className="label-mono mb-2">Spotify artist</div>
          <div className="font-display text-2xl mb-4">Your catalog source</div>
          <p className="text-white/55 text-sm mb-5 leading-relaxed">
            Paste your Spotify <span className="text-white">artist page URL</span> (open.spotify.com/artist/…) —
            Alchemist Lab pulls your real albums + singles in place of any mock data.
          </p>
          {savedArtistId && (
            <div className="flex items-center gap-2 mb-4 text-sm text-[#00FF66]" data-testid="settings-artist-saved">
              <Check size={14} /> Artist ID: <span className="font-mono text-white/80">{savedArtistId}</span>
            </div>
          )}
          <div className="flex items-center gap-2 mb-1">
            <LinkIcon size={14} className="text-white/35" />
            <input
              type="url"
              className="mp-input"
              placeholder="https://open.spotify.com/artist/..."
              value={artistUrl}
              onChange={(e) => setArtistUrl(e.target.value)}
              data-testid="settings-artist-input"
            />
          </div>
          <button
            className="btn-amber-small mt-4"
            onClick={saveArtist}
            disabled={saving}
            data-testid="settings-artist-save"
          >
            {saving ? "Syncing…" : savedArtistId ? "Re-sync" : "Sync releases"}
          </button>
        </div>

        <div className="panel p-5 sm:p-8 md:col-span-2">
          <div className="label-mono mb-2">AI video — artist description</div>
          <div className="font-display text-2xl mb-4">How Sora 2 sees you</div>
          <p className="text-white/55 text-sm mb-5 leading-relaxed">
            Describe yourself in one sentence — Sora 2 will use this when generating videos
            that feature you performing or producing. Be specific about visual traits
            (build, hair, wardrobe, vibe) — <span className="text-white">color palette stays locked to vintage black / grey / white</span> across all posts.
          </p>
          <textarea
            className="mp-textarea"
            rows={3}
            maxLength={600}
            placeholder="e.g. A tall male electronic music producer in his 30s, short dark hair, wearing a black hoodie, minimal gold chain, quiet and focused energy."
            value={artistDescription}
            onChange={(e) => { setArtistDescription(e.target.value); setDescDirty(true); }}
            data-testid="settings-artist-description"
          />
          <div className="flex items-center justify-between mt-3">
            <div className="label-mono text-white/35">
              {artistDescription.length}/600 · Leave blank for atmospheric-only clips (no person).
            </div>
            <button
              className="btn-amber-small"
              onClick={saveDescription}
              disabled={savingDesc || !descDirty}
              data-testid="settings-description-save"
            >
              {savingDesc ? "Saving…" : "Save description"}
            </button>
          </div>
        </div>

        <div className="panel p-5 sm:p-8 md:col-span-2">
          <div className="flex items-center gap-2 mb-2">
            <Hash size={14} className="text-[#FF3B00]" />
            <div className="label-mono">Hashtag editor</div>
          </div>
          <div className="font-display text-2xl mb-4">Your custom rotation pool</div>
          <p className="text-white/55 text-sm mb-5 leading-relaxed">
            Tags below get <span className="text-white">injected into every auto-generated caption</span> on top of the genre preset.
            Hit <span className="font-mono text-white/80">Enter</span> or <span className="font-mono text-white/80">,</span> to add. Paste space-separated lists to bulk-add.
          </p>

          <div className="grid md:grid-cols-[1fr_auto] gap-4 items-start">
            <div>
              <div className="border border-white/10 bg-white/[0.02] p-3 min-h-[120px] flex flex-wrap gap-2">
                {customHashtags.map((t) => (
                  <span
                    key={t}
                    className="inline-flex items-center gap-1.5 bg-[#FF3B00]/10 border border-[#FF3B00]/30 text-[#FF3B00] px-2.5 py-1 text-xs font-mono"
                    data-testid={`settings-hashtag-chip-${t.slice(1)}`}
                  >
                    {t}
                    <button
                      type="button"
                      onClick={() => removeTag(t)}
                      className="hover:text-white"
                      aria-label={`Remove ${t}`}
                      data-testid={`settings-hashtag-remove-${t.slice(1)}`}
                    >
                      <X size={11} />
                    </button>
                  </span>
                ))}
                <input
                  type="text"
                  className="bg-transparent flex-1 min-w-[140px] outline-none text-sm text-white placeholder-white/25 px-1"
                  placeholder={customHashtags.length ? "" : "#melodichouse #alchemist #yourlabel"}
                  value={tagDraft}
                  onChange={(e) => setTagDraft(e.target.value)}
                  onKeyDown={onTagKey}
                  onPaste={onTagPaste}
                  data-testid="settings-hashtag-input"
                />
              </div>
              <div className="label-mono text-white/35 mt-2">
                {customHashtags.length}/50 · letters, numbers and underscores only · max 32 chars per tag
              </div>
            </div>

            <div className="md:w-[260px]">
              <div className="label-mono mb-2">Default genre</div>
              <select
                className="mp-input"
                value={defaultGenre}
                onChange={(e) => setDefaultGenre(e.target.value)}
                data-testid="settings-default-genre"
              >
                {GENRES.map((g) => (
                  <option key={g.key} value={g.key}>{g.label}</option>
                ))}
              </select>
              <div className="label-mono text-white/35 mt-2 leading-relaxed">
                Used when a release has no obvious genre keyword in its notes.
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-5">
            <button
              className="btn-ghost-line"
              onClick={suggestTags}
              disabled={suggesting}
              data-testid="settings-hashtags-suggest"
            >
              <Wand2 size={13} />
              {suggesting ? "Thinking…" : "Suggest with AI"}
            </button>
            <button
              className="btn-amber-small"
              onClick={saveTags}
              disabled={savingTags}
              data-testid="settings-hashtags-save"
            >
              {savingTags ? "Saving…" : "Save hashtag preferences"}
            </button>
          </div>
        </div>

        <div className="panel p-5 sm:p-8 md:col-span-2">
          <div className="flex items-center gap-2 mb-2">
            <Lock size={14} className="text-[#FF3B00]" />
            <div className="label-mono">Security</div>
          </div>
          <div className="font-display text-2xl mb-4">Change password</div>
          <form onSubmit={submitPassword} className="grid md:grid-cols-3 gap-4 max-w-3xl">
            <div className="md:col-span-3 relative">
              <div className="label-mono mb-2">Current password</div>
              <input
                type={pwShow ? "text" : "password"}
                className="mp-input pr-10"
                value={pwCurrent}
                onChange={(e) => setPwCurrent(e.target.value)}
                required
                autoComplete="current-password"
                data-testid="settings-pw-current"
              />
              <button
                type="button"
                onClick={() => setPwShow(!pwShow)}
                className="absolute right-3 top-[34px] text-white/45 hover:text-white"
                aria-label="Toggle password visibility"
              >
                {pwShow ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
            <div>
              <div className="label-mono mb-2">New password</div>
              <input
                type={pwShow ? "text" : "password"}
                className="mp-input"
                value={pwNew}
                onChange={(e) => setPwNew(e.target.value)}
                required
                autoComplete="new-password"
                data-testid="settings-pw-new"
              />
            </div>
            <div>
              <div className="label-mono mb-2">Confirm new</div>
              <input
                type={pwShow ? "text" : "password"}
                className="mp-input"
                value={pwConfirm}
                onChange={(e) => setPwConfirm(e.target.value)}
                required
                autoComplete="new-password"
                data-testid="settings-pw-confirm"
              />
            </div>
            <div className="flex items-end">
              <button
                type="submit"
                className="btn-amber-small w-full"
                disabled={savingPw || !pwCurrent || !pwNew || !pwConfirm}
                data-testid="settings-pw-submit"
              >
                {savingPw ? "Updating…" : "Update password"}
              </button>
            </div>
            <div className="md:col-span-3 label-mono text-white/35">
              Min 8 characters · use letters + numbers · stays signed-in on this device.
            </div>
          </form>
        </div>

        <div className="panel p-5 sm:p-8 md:col-span-2">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles size={14} className="text-[#FF3B00]" />
            <div className="label-mono">Batch render speed</div>
          </div>
          <div className="font-display text-2xl mb-4">Weekly batch &amp; release-arc reels</div>
          <p className="text-white/55 text-sm mb-5 leading-relaxed">
            Video source used when the autopilot generates Reels / Shorts in bulk
            (Generate&nbsp;weekly&nbsp;batch + release Blast). <span className="text-white">Custom posts</span> ignore this and use their own per-post picker.
          </p>
          <div className="label-mono text-white/35 mb-2">Google Veo 3.1 · rendered through your Emergent Universal Key</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-xl">
            {RENDER_SPEEDS.map(({ id, label, Icon, hint, recommended }) => (
              <button
                key={id}
                type="button"
                onClick={() => saveBatchSpeed(id)}
                disabled={savingSpeed}
                data-testid={`settings-batch-speed-${id}`}
                className={`relative p-3 border flex items-center gap-3 transition-colors text-left ${
                  batchSpeed === id
                    ? "border-[#FF3B00] bg-[#FF3B00]/10 text-white"
                    : "border-white/10 text-white/60 hover:text-white hover:border-white/25"
                } disabled:opacity-50`}
                title={hint}
              >
                {recommended && (
                  <span className="absolute -top-2 left-3 text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 bg-[#FF3B00] text-black">
                    Default
                  </span>
                )}
                <Icon size={16} strokeWidth={1.4} className="shrink-0" />
                <span className="text-[11px] font-mono uppercase tracking-wider">{label}</span>
              </button>
            ))}
          </div>
          <div className="label-mono text-white/35 mt-3 leading-relaxed max-w-xl">
            {RENDER_SPEEDS.find((s) => s.id === batchSpeed)?.hint}
          </div>
        </div>

        <div className="panel p-5 sm:p-8 md:col-span-2">
          <div className="label-mono mb-6">Autopilot preferences</div>
          <ul className="space-y-4 text-sm text-white/75 grid md:grid-cols-2 gap-4 md:gap-y-0">
            <li className="flex items-center justify-between border-b border-white/10 pb-3">
              <span>Posts per week</span><span className="font-mono text-white">5</span>
            </li>
            <li className="flex items-center justify-between border-b border-white/10 pb-3">
              <span>Peak hour — IG</span><span className="font-mono text-white">19:00</span>
            </li>
            <li className="flex items-center justify-between border-b border-white/10 pb-3">
              <span>Peak hour — FB</span><span className="font-mono text-white">13:00</span>
            </li>
            <li className="flex items-center justify-between border-b border-white/10 pb-3">
              <span>Peak hour — YT</span><span className="font-mono text-white">17:00</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}
